import React, { useEffect, useState } from "react";
import styles from './SignupForm.module.scss';
import Logo from "../logo/Logo";
import TextField from "../textField/TextField";
import Alert from "../alert/Alert";
import AutoComplete from "../autoComplete/AutoComplete";
import Button from "../button/Button";
import PropTypes from "prop-types";
import MultiSelectDropdown from "../multiSelectDropdown/MultiSelectDropdown";
import { useToasts } from "../../contexts/ToastContext";

const SignupForm = ({
  formData = {},
  healthSystemList = [],
  onHealthSystemChange,
  clinicalSiteList = [],
  specialityList = [],
  onSubmit,
  error
}) => {
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [healthSystemIds, setHealthSystemIds] = useState([]);
  const [healthSystemIdsError, setHealthSystemIdsError] = useState('');
  const [clinicalSiteIds, setClinicalSiteIds] = useState([]);
  const [clinicalSiteIdsError, setClinicalSiteIdsError] = useState('');
  const [speciality, setSpeciality] = useState('');
  const [specialityError, setSpecialityError] = useState('');
  const [specialityIds, setSpecialityIds] = useState([]);
  const [specialityIdsError, setSpecialityIdsError] = useState('');
  const [role, setRole] = useState('');
  const { showToast } = useToasts();

  useEffect(() => {
    setFirstName(formData.firstName || '');
    setLastName(formData.lastName || '');
    setHealthSystemIds(formData.healthSystemIds || []);
    setClinicalSiteIds(formData.clinicalSiteIds || []);
    setSpeciality(formData.speciality || '');
    setSpecialityIds(formData.specialityIds || []);
    setRole(formData.role || '');
  }, [formData]);
  
  const validateFirstName = () => {
    if(!firstName) {
      return 'First Name is required';
    }
    if(!/^[A-Za-z]{2}/.test(firstName)) {
      return 'First Name must contain only letters and be at least 2 characters long.'
    }
    return '';
  }
  
  const validateLastName = () => {
    if(!lastName) {
      return 'Last Name is required';
    }
    if(!/^[A-Za-z]{2}/.test(lastName)) {
      return 'Last Name must contain only letters and be at least 2 characters long.'
    }
    return '';
  }
  
  const validateHealthSystemIds = () => {
    if(healthSystemIds.length === 0) {
      return 'Health System is required';
    }
    return '';
  }
  
  const validateClinicalSiteIds = () => {
    if(clinicalSiteIds.length === 0) {
      return 'Clinical Site is required';
    }
    return '';
  }
  
  const validateSpeciality = () => {
    if(!speciality) {
      return 'Specialty is required';
    }
    return '';
  }
  
  const validateSpecialityIds = () => {
    if(specialityIds.length === 0) {
      return 'Specialty is required';
    }
    return '';
  }

  const hanldeSignup = () => {
    const firstNameValidationResult = validateFirstName();
    const lastNameValidationResult = validateLastName();
    const healthSystemIdsValidationResult = role === 'Instructor' ? validateHealthSystemIds() : '';
    const clinicalSiteIdsValidationResult = role === 'Instructor' ? validateClinicalSiteIds() : '';
    const specialityValidationResult = role === 'Instructor' ? validateSpeciality() : '';
    const specialityIdsValidationResult = role === 'Student' ? validateSpecialityIds() : '';

    setFirstNameError(firstNameValidationResult);
    setLastNameError(lastNameValidationResult);
    if(role === 'Instructor') {
      setHealthSystemIdsError(healthSystemIdsValidationResult);
      setClinicalSiteIdsError(clinicalSiteIdsValidationResult);
      setSpecialityError(specialityValidationResult);
    }
    if(role === 'Student') {
      setSpecialityIdsError(specialityIdsValidationResult);
    }

    if (firstNameValidationResult || lastNameValidationResult ||
        healthSystemIdsValidationResult || clinicalSiteIdsValidationResult ||
        specialityValidationResult || specialityIdsValidationResult) {
      return false;
    }

    let data = {
      firstName,
      lastName,
    }

    if(role === 'Instructor') {
      data = {
        ...data,
        healthSystemIds,
        clinicalSiteIds,
        speciality
      }
    }

    if(role === 'Student') {
      data = {
        ...data,
        specialityIds
      }
    }

    onSubmit(data);
  };

  return (
    <div className={styles.container}>
      <Logo />
      <h1 className={styles.title}>Welcome to Maestro!</h1>
      { error && <Alert message={error} type="error" /> }
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          hanldeSignup();
        }}
      >
        <TextField
          label="First Name"
          value={firstName}
          onChange={(value) => setFirstName(value)}
          error={firstNameError}
        />
        <TextField
          label="Last Name"
          value={lastName}
          onChange={(value) => setLastName(value)}
          error={lastNameError}
        />
        {role === 'Instructor' && <>
          <MultiSelectDropdown
            label="Health System"
            options={healthSystemList}
            selected={healthSystemIds}
            onChange={(selected) => {
              setHealthSystemIds(selected);
              onHealthSystemChange(selected);
            }}
            error={healthSystemIdsError}
          />
          <MultiSelectDropdown
            label="Clinical Site"
            options={clinicalSiteList}
            selected={clinicalSiteIds}
            onChange={(selected) => {
              setClinicalSiteIds(selected);
            }}
            error={clinicalSiteIdsError}
          />
          <AutoComplete
            label="Specialty"
            value={speciality}
            onChange={(value) => setSpeciality(value)}
            error={specialityError}
            list={specialityList.map(item => item.label)}
          />
        </>}
        {role === 'Student' && <MultiSelectDropdown
          label="Specialty(s) of Interest"
          options={specialityList}
          selected={specialityIds}
          onChange={(selected) => {
            if(selected.length > 3) {
              showToast('You can select up to 3 specialities only.', 'error')
            } else {
              setSpecialityIds(selected);
            }
          }}
          error={specialityIdsError}
        />}
        <div className={styles.submit}>
          <Button
            label="Proceed"
            type="submit"
          />
        </div>
      </form>
    </div>
  );
};

SignupForm.propTypes = {
  formData: PropTypes.object,
  healthSystemList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  onHealthSystemChange: PropTypes.func.isRequired,
  clinicalSiteList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  specialityList: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
};

export default SignupForm;